import { chains } from "@/src/evm/chains/index.js";
import { Protocol } from "@/src/evm/enums.js";
import { resolvers } from "@/src/evm/releases/resolvers.js";
import type { Sablier } from "@/src/types.js";
import aliases from "./aliases.js";
import manifest from "./manifest.js";

function get(chainId: number, contractMap: Sablier.EVM.ContractMap): Sablier.EVM.Deployment {
  return resolvers.deployment.standard({
    aliasMap: aliases,
    chainId,
    contractMap,
    protocol: Protocol.Lockup,
    version: "v2.0",
  });
}

/**
 * @description Mainnet deployments for Lockup v2.0
 */
export const mainnets: Sablier.EVM.Deployment[] = [
  get(chains.abstract.id, {
    [manifest.HELPERS]: "0x07c194dFE7DCe9Ae7Ffe4bF32683cf1F8CDD4aEa",
    [manifest.LOCKUP_NFT_DESCRIPTOR]: "0x63Ff2E370788C163D5a1909B5FCb299DB327AEF9",
    [manifest.SABLIER_BATCH_LOCKUP]: "0x3409308357BB704f79f70d748da502F363Dc2f1D",
    [manifest.SABLIER_LOCKUP]: ["0x14Eb4AB47B2ec2a71763eaBa202a252E176FAE88", 326_658],
    [manifest.VESTING_MATH]: "0xbB2e2884AE40003BB55fd3A85A9f8f7f72Aa441F",
  }),
  get(chains.arbitrum.id, {
    [manifest.HELPERS]: "0xf8076E4Fb5cfE8be1C26E61222DC51828Db8C1dc",
    [manifest.LOCKUP_NFT_DESCRIPTOR]: "0xd5c6a0Dd2E1822865c308850b8b3E2CcE762D061",
    [manifest.SABLIER_BATCH_LOCKUP]: "0xB11Ead48F572155C5F8dB6201701e91A936896f7",
    [manifest.SABLIER_LOCKUP]: ["0x467D5Bf8Cfa1a5f99328fBdCb9C751c78934b725", 299_856_292],
    [manifest.VESTING_MATH]: "0x5522CA06Ce080800AB59BA4C091e63f6f54C5E6d",
  }),
  get(chains.avalanche.id, {
    [manifest.HELPERS]: "0xf8076E4Fb5cfE8be1C26E61222DC51828Db8C1dc",
    [manifest.LOCKUP_NFT_DESCRIPTOR]: "0x906A4BD5dD0EF13654eA29bFD6185d0d64A4b674",
    [manifest.SABLIER_BATCH_LOCKUP]: "0xABDE228d84D86D78029C31A37Ae2435C8f923c8b",
    [manifest.SABLIER_LOCKUP]: ["0x3C81BBBe72EF8eF3fb1D19B0bd6310Ad0dd27E82", 56_433_740],
    [manifest.VESTING_MATH]: "0x5522CA06Ce080800AB59BA4C091e63f6f54C5E6d",
  }),
  get(chains.base.id, {
    [manifest.HELPERS]: "0xf8076E4Fb5cfE8be1C26E61222DC51828Db8C1dc",
    [manifest.LOCKUP_NFT_DESCRIPTOR]: "0x87e437030b7439150605a641483de98672E26317",
    [manifest.SABLIER_BATCH_LOCKUP]: "0xC26CdAFd6ec3c91AD9aEeB237Ee1f37205ED26a4",
    [manifest.SABLIER_LOCKUP]: ["0xb5D78DD3276325f5FAF3106Cc4Acc56E28e0Fe3B", 25_601_436],
    [manifest.VESTING_MATH]: "0x5522CA06Ce080800AB59BA4C091e63f6f54C5E6d",
  }),
  get(chains.berachain.id, {
    [manifest.HELPERS]: "0xf8076E4Fb5cfE8be1C26E61222DC51828Db8C1dc",
    [manifest.LOCKUP_NFT_DESCRIPTOR]: "0x3bbE0a21792564604B0fDc00019532Adeffa70eb",
    [manifest.SABLIER_BATCH_LOCKUP]: "0x75838C66Dfa2296bB9758f75fC7ad219718C8a88",
    [manifest.SABLIER_LOCKUP]: ["0xC19A2542156b5d7960e0eF46E9787E7d336cF428", 780_094],
    [manifest.VESTING_MATH]: "0x5522CA06Ce080800AB59BA4C091e63f6f54C5E6d",
  }),
  get(chains.blast.id, {
    [manifest.HELPERS]: "0xf8076E4Fb5cfE8be1C26E61222DC51828Db8C1dc",
    [manifest.LOCKUP_NFT_DESCRIPTOR]: "0x959c412d5919b1Ec5D07bee3443ea68c91d57dd7",
    [manifest.SABLIER_BATCH_LOCKUP]: "0x193c2af965FEAca8D893c974712e5b6BD3cBc5ec",
    [manifest.SABLIER_LOCKUP]: ["0xDbB6e9653d7e41766712Db22eB08ED3F21009fdd", 14_593_162],
    [manifest.VESTING_MATH]: "0x5522CA06Ce080800AB59BA4C091e63f6f54C5E6d",
  }),
  get(chains.bsc.id, {
    [manifest.HELPERS]: "0xf8076E4Fb5cfE8be1C26E61222DC51828Db8C1dc",
    [manifest.LOCKUP_NFT_DESCRIPTOR]: "0x56831a5a932793E02251126831174Ab8Bf2f7695",
    [manifest.SABLIER_BATCH_LOCKUP]: "0xcf990fA3267F0945bBf7cf40A0c03F9dFE6a1804",
    [manifest.SABLIER_LOCKUP]: ["0x6E0baD2c077d699841F1929b45bfb93FAfBEd395", 46_137_049],
    [manifest.VESTING_MATH]: "0x5522CA06Ce080800AB59BA4C091e63f6f54C5E6d",
  }),
  get(chains.chiliz.id, {
    [manifest.HELPERS]: "0x6FcAB41e3b62d05aB4fC729586CB06Af2a2662D0",
    [manifest.LOCKUP_NFT_DESCRIPTOR]: "0x8A96f827082FB349B6e268baa0a7A5584c4Ccda6",
    [manifest.SABLIER_BATCH_LOCKUP]: "0x179536f3289fb50076968b339C7EF0Dc0B38E3AF",
    [manifest.SABLIER_LOCKUP]: ["0x711900e5f55d427cd88e5E3FCAe54Ccf02De71F4", 20_430_940],
    [manifest.VESTING_MATH]: "0x84f092cf4D7D36C2d4987F672df81a39200a7146",
  }),
  get(chains.coreDao.id, {
    [manifest.HELPERS]: "0xf8076E4Fb5cfE8be1C26E61222DC51828Db8C1dc",
    [manifest.LOCKUP_NFT_DESCRIPTOR]: "0xac0cf0f2a96ed7ec3cfa4d0be621c67adc9dd903",
    [manifest.SABLIER_BATCH_LOCKUP]: "0x96dadeeab25413de04a1b8e40c4de41bd9d7fd29",
    [manifest.SABLIER_LOCKUP]: ["0x4fff53bfe86a0bd59a81c89d8ba84c67cf947764", 21_574_032],
    [manifest.VESTING_MATH]: "0x5522CA06Ce080800AB59BA4C091e63f6f54C5E6d",
  }),
  get(chains.gnosis.id, {
    [manifest.HELPERS]: "0xf8076E4Fb5cfE8be1C26E61222DC51828Db8C1dc",
    [manifest.LOCKUP_NFT_DESCRIPTOR]: "0x3140a6900AA2FF3186730741ad8255ee4e6d8Ff1",
    [manifest.SABLIER_BATCH_LOCKUP]: "0xe89EE0b2B31A296C5cCb631C3670F94bDD64a0D2",
    [manifest.SABLIER_LOCKUP]: ["0x007aF5dC7b1CaA66Cf7Ebcc01E2e6ba4D55D3e92", 38_256_772],
    [manifest.VESTING_MATH]: "0x5522CA06Ce080800AB59BA4C091e63f6f54C5E6d",
  }),
  get(chains.hyperevm.id, {
    [manifest.HELPERS]: "0xe2f66eEe2E227c40074668ba53021423ED7D4ea1",
    [manifest.LOCKUP_NFT_DESCRIPTOR]: "0x7263d77e9e872f82A15e5E1a9816440D23758708",
    [manifest.SABLIER_BATCH_LOCKUP]: "0x5444eA1B8B636A8FdF7cE814077E664c28dE30Ec",
    [manifest.SABLIER_LOCKUP]: ["0x856167EE3e09Ba562d69A542Ab6A939903ad738e", 8_594_061],
    [manifest.VESTING_MATH]: "0xf91e1a8fA643F8062C12Ca2865f1eb2258d6422F",
  }),
  get(chains.lightlink.id, {
    [manifest.HELPERS]: "0xf8076E4Fb5cfE8be1C26E61222DC51828Db8C1dc",
    [manifest.LOCKUP_NFT_DESCRIPTOR]: "0xCFB5F90370A7884DEc59C55533782B45FA24f4d1",
    [manifest.SABLIER_BATCH_LOCKUP]: "0xe8fa70D0172BB36c952E3e20e2f3550Ca4557761",
    [manifest.SABLIER_LOCKUP]: ["0x6c65aAf03186d1DA60127D3d7792cF36eD99D909", 125_509_194],
    [manifest.VESTING_MATH]: "0x5522CA06Ce080800AB59BA4C091e63f6f54C5E6d",
  }),
  get(chains.linea.id, {
    [manifest.HELPERS]: "0x058aD99662FE7ecB8c3109920C99439a302b6573",
    [manifest.LOCKUP_NFT_DESCRIPTOR]: "0x1514a869D29a8B22961e8F9eBa3DC64000b96BCe",
    [manifest.SABLIER_BATCH_LOCKUP]: "0xEdf0A4b30defD14449604d1b97e2c39128c136CA",
    [manifest.SABLIER_LOCKUP]: ["0x6964252561e8762dD10267176EaC5078b6291e51", 15_111_716],
    [manifest.VESTING_MATH]: "0xdEe57959770667d97A90C94fE70C055496B7a791",
  }),
  get(chains.mainnet.id, {
    [manifest.HELPERS]: "0xf8076E4Fb5cfE8be1C26E61222DC51828Db8C1dc",
    [manifest.LOCKUP_NFT_DESCRIPTOR]: "0xA9dC6878C979B5cc1d98a1803F0664ad725A1f56",
    [manifest.SABLIER_BATCH_LOCKUP]: "0x3F6E8a8Cffe377c4649aCeB01e6F20c60fAA356c",
    [manifest.SABLIER_LOCKUP]: ["0x7C01AA3783577E15fD7e272443D44B92d5b21056", 21_719_028],
    [manifest.VESTING_MATH]: "0x5522CA06Ce080800AB59BA4C091e63f6f54C5E6d",
  }),
  get(chains.mode.id, {
    [manifest.HELPERS]: "0xf8076E4Fb5cfE8be1C26E61222DC51828Db8C1dc",
    [manifest.LOCKUP_NFT_DESCRIPTOR]: "0x64e7879558b6dfE2f510bd4b9Ad196ef0371EAA8",
    [manifest.SABLIER_BATCH_LOCKUP]: "0x34dBab20FD097F63DDbf3092D83B1005D2573082",
    [manifest.SABLIER_LOCKUP]: ["0x3aEbaDFC423fD08BE4715986F68D5E9A597ec974", 18_912_617],
    [manifest.VESTING_MATH]: "0x5522CA06Ce080800AB59BA4C091e63f6f54C5E6d",
  }),
  get(chains.morph.id, {
    [manifest.HELPERS]: "0xf8076E4Fb5cfE8be1C26E61222DC51828Db8C1dc",
    [manifest.LOCKUP_NFT_DESCRIPTOR]: "0x660314f09ac3B65E216B6De288aAdc2599AF14e2",
    [manifest.SABLIER_BATCH_LOCKUP]: "0x081BBbd4861BaBACE3E7eDC8a45741129DfC02fE",
    [manifest.SABLIER_LOCKUP]: ["0xF3cd08105b6745965149eF02b8aBdCEa0Ae51241", 4_034_472],
    [manifest.VESTING_MATH]: "0x5522CA06Ce080800AB59BA4C091e63f6f54C5E6d",
  }),
  get(chains.optimism.id, {
    [manifest.HELPERS]: "0xf8076E4Fb5cfE8be1C26E61222DC51828Db8C1dc",
    [manifest.LOCKUP_NFT_DESCRIPTOR]: "0x41dBa1AfBB6DF91b3330dc009842327A9858Cbae",
    [manifest.SABLIER_BATCH_LOCKUP]: "0x0c4Cd6087DbFa3F74661BAbbFaa35273baC1c4b1",
    [manifest.SABLIER_LOCKUP]: ["0x822e9c4852E978104d82F0f785bFA663c2b700c1", 131_196_856],
    [manifest.VESTING_MATH]: "0x5522CA06Ce080800AB59BA4C091e63f6f54C5E6d",
  }),
  get(chains.polygon.id, {
    [manifest.HELPERS]: "0xf8076E4Fb5cfE8be1C26E61222DC51828Db8C1dc",
    [manifest.LOCKUP_NFT_DESCRIPTOR]: "0xf5e12d0bA25FCa0D738Ec57f149736B2e4C46980",
    [manifest.SABLIER_BATCH_LOCKUP]: "0x1aDd9385F2C5c8e446bbB77c7A36839aB7743AF4",
    [manifest.SABLIER_LOCKUP]: ["0xE0BFe071Da104e571298f8b6e0fcE44C512C1Ff4", 67_212_728],
    [manifest.VESTING_MATH]: "0x5522CA06Ce080800AB59BA4C091e63f6f54C5E6d",
  }),
  get(chains.scroll.id, {
    [manifest.HELPERS]: "0xf8076E4Fb5cfE8be1C26E61222DC51828Db8C1dc",
    [manifest.LOCKUP_NFT_DESCRIPTOR]: "0x00Ff6443E902874924dd217c1435e3be04f57431",
    [manifest.SABLIER_BATCH_LOCKUP]: "0x8234Ad3CC4D29a4619C36a15286dac73078672a8",
    [manifest.SABLIER_LOCKUP]: ["0xcB0B1f1D116eD62135848d8C90EB61afDA936Da8", 13_007_957],
    [manifest.VESTING_MATH]: "0x5522CA06Ce080800AB59BA4C091e63f6f54C5E6d",
  }),
  get(chains.sei.id, {
    [manifest.HELPERS]: "0xf8076E4Fb5cfE8be1C26E61222DC51828Db8C1dc",
    [manifest.LOCKUP_NFT_DESCRIPTOR]: "0xeaFB40669fe3523b073904De76410b46e79a56D7",
    [manifest.SABLIER_BATCH_LOCKUP]: "0x34686937bef23c6441248Cc5A63A79a3a707e7E4",
    [manifest.SABLIER_LOCKUP]: ["0x736A6E895790e089aEC2Bf76B2D7f368ce6Efff5", 138_913_911],
    [manifest.VESTING_MATH]: "0x5522CA06Ce080800AB59BA4C091e63f6f54C5E6d",
  }),
  get(chains.sonic.id, {
    [manifest.HELPERS]: "0xf8076E4Fb5cfE8be1C26E61222DC51828Db8C1dc",
    [manifest.LOCKUP_NFT_DESCRIPTOR]: "0xAE8FE47765f88e0A2D7E8fbaf33583DBf0373f01",
    [manifest.SABLIER_BATCH_LOCKUP]: "0xc91b20dC29E19120BF3f54277947327AfD50fBa2",
    [manifest.SABLIER_LOCKUP]: ["0xc37462eE6500D2C36c9131b921fAADBD6cb7B60b", 43_943_890],
    [manifest.VESTING_MATH]: "0x5522CA06Ce080800AB59BA4C091e63f6f54C5E6d",
  }),
  get(chains.sophon.id, {
    [manifest.HELPERS]: "0x424B27529B49EF4Bfa0727aFcFE4387Ac2932944",
    [manifest.LOCKUP_NFT_DESCRIPTOR]: "0xAc2E42b520364940c90Ce164412Ca9BA212d014B",
    [manifest.SABLIER_BATCH_LOCKUP]: "0x7282d83E49363f373102d195F66649eBD6C57B9B",
    [manifest.SABLIER_LOCKUP]: ["0x28fCAE6bda2546C93183EeC8638691B2EB184003", 11_275_708],
    [manifest.VESTING_MATH]: "0x9971914DA16787F6cCfb27bEfB4404e33C8b869D",
  }),
  get(chains.superseed.id, {
    [manifest.HELPERS]: "0xf8076E4Fb5cfE8be1C26E61222DC51828Db8C1dc",
    [manifest.LOCKUP_NFT_DESCRIPTOR]: "0xa4576b58Ec760A8282D081dc94F3dc716DFc61e9",
    [manifest.SABLIER_BATCH_LOCKUP]: "0x89e9F2473836d9ab7D28Df6F180E30992b8CB5d6",
    [manifest.SABLIER_LOCKUP]: ["0xF46D1f8C85f215A515F6D738ab3E3bA081f6C083", 5_908_538],
    [manifest.VESTING_MATH]: "0x5522CA06Ce080800AB59BA4C091e63f6f54C5E6d",
  }),
  get(chains.taiko.id, {
    [manifest.HELPERS]: "0xf8076E4Fb5cfE8be1C26E61222DC51828Db8C1dc",
    [manifest.LOCKUP_NFT_DESCRIPTOR]: "0x79F1fD8bB2D455f64010063Fc79E27561980FE10",
    [manifest.SABLIER_BATCH_LOCKUP]: "0xcBbA08768C4a9D9131dE0467Ae136b8450dC13B2",
    [manifest.SABLIER_LOCKUP]: ["0x628E88cDF558c0F4796c8CeB5068a023a7159aA7", 798_873],
    [manifest.VESTING_MATH]: "0x5522CA06Ce080800AB59BA4C091e63f6f54C5E6d",
  }),
  get(chains.tangle.id, {
    [manifest.HELPERS]: "0x3D664B2Da905DDD0Db931982FD9a759ea950D6e1",
    [manifest.LOCKUP_NFT_DESCRIPTOR]: "0x92FC05e49c27884d554D98a5C01Ff0894a9DC29a",
    [manifest.SABLIER_BATCH_LOCKUP]: "0xF5AC60870E1CCc4Bfce23cfbb7a796A0d8dBAf47",
    [manifest.SABLIER_LOCKUP]: ["0x1cAe76b71913598d7664d16641CCB6037d8Ed61a", 4_003_841],
    [manifest.VESTING_MATH]: "0x9EfC8663cAB0e2d97ad17C9fbfc8392445517E94",
  }),
  get(chains.unichain.id, {
    [manifest.HELPERS]: "0xf8076e4fb5cfe8be1c26e61222dc51828db8c1dc",
    [manifest.LOCKUP_NFT_DESCRIPTOR]: "0xa5F12D63E18a28C9BE27B6f3d91ce693320067ba",
    [manifest.SABLIER_BATCH_LOCKUP]: "0xaf875A2bDb74bA8872292FC371131eb45a9b570C",
    [manifest.SABLIER_LOCKUP]: ["0x26C341C4D79bA8F6BFB450a49E9165c936316B14", 13_882_080],
    [manifest.VESTING_MATH]: "0x5522ca06ce080800ab59ba4c091e63f6f54c5e6d",
  }),
  get(chains.xdc.id, {
    [manifest.HELPERS]: "0xf8076e4fb5cfe8be1c26e61222dc51828db8c1dc",
    [manifest.LOCKUP_NFT_DESCRIPTOR]: "0x4c1311a9d88BFb7023148aB04F7321C2E91c29bf",
    [manifest.SABLIER_BATCH_LOCKUP]: "0x6d36227Dd84e2A3d898B192Bc82a005c3cc2320C",
    [manifest.SABLIER_LOCKUP]: ["0x489e0DC5E62A751A2b209f1cC03E189fd6257176", 85_225_620],
    [manifest.VESTING_MATH]: "0x5522ca06ce080800ab59ba4c091e63f6f54c5e6d",
  }),
  get(chains.zksync.id, {
    [manifest.HELPERS]: "0x37De3Fc44a07A40411AD0Cea4310990C9F88c1C1",
    [manifest.LOCKUP_NFT_DESCRIPTOR]: "0xCB2d53c58496C2aA114bce4ED5C7fe768ce86542",
    [manifest.SABLIER_BATCH_LOCKUP]: "0xCC926359DBE6b6311D63f8155fcC3B57F3fAAE80",
    [manifest.SABLIER_LOCKUP]: ["0x7BCcB3595Aa81Dbe8A69DD8C46f5C2A3cf76594A", 54_967_807],
    [manifest.VESTING_MATH]: "0xEE4a32E026aC2FD6BF71d9D7eB00803576aD314d",
  }),
];

/**
 * @description Testnet deployments for Lockup v2.0
 */
export const testnets: Sablier.EVM.Deployment[] = [
  get(chains.arbitrumSepolia.id, {
    [manifest.HELPERS]: "0xf8076E4Fb5cfE8be1C26E61222DC51828Db8C1dc",
    [manifest.LOCKUP_NFT_DESCRIPTOR]: "0x8224eb5D7d76B2D7Df43b868D875E79B11500eA8",
    [manifest.SABLIER_BATCH_LOCKUP]: "0xbf85cD17cA59b7A2b81d3D776cE1602a7C0aF9D9",
    [manifest.SABLIER_LOCKUP]: ["0x83Dd52FCA44E069020b58155b761A590F12B59d3", 119_019_039],
    [manifest.VESTING_MATH]: "0x5522CA06Ce080800AB59BA4C091e63f6f54C5E6d",
  }),
  get(chains.baseSepolia.id, {
    [manifest.HELPERS]: "0xf8076E4Fb5cfE8be1C26E61222DC51828Db8C1dc",
    [manifest.LOCKUP_NFT_DESCRIPTOR]: "0xCA2593027BA24856c292Fdcb5F987E0c25e755a4",
    [manifest.SABLIER_BATCH_LOCKUP]: "0xEdc716E9672f672456d22b02532395c1e62B8C16",
    [manifest.SABLIER_LOCKUP]: ["0xa4777CA525d43a7aF55D45b11b430606d7416f8d", 21_166_223],
    [manifest.VESTING_MATH]: "0x5522CA06Ce080800AB59BA4C091e63f6f54C5E6d",
  }),

  get(chains.lineaSepolia.id, {
    [manifest.HELPERS]: "0x058aD99662FE7ecB8c3109920C99439a302b6573",
    [manifest.LOCKUP_NFT_DESCRIPTOR]: "0xCE94BE25320A51Ac868d0C133c251aE10682DabD",
    [manifest.SABLIER_BATCH_LOCKUP]: "0x9A987181BF05b7C154118A3216d522fa2407a8Be",
    [manifest.SABLIER_LOCKUP]: ["0xbb4A14868A4BEc78b7354582b8C818ba520d7A4E", 8_618_209],
    [manifest.VESTING_MATH]: "0xdEe57959770667d97A90C94fE70C055496B7a791",
  }),

  get(chains.optimismSepolia.id, {
    [manifest.HELPERS]: "0xf8076E4Fb5cfE8be1C26E61222DC51828Db8C1dc",
    [manifest.LOCKUP_NFT_DESCRIPTOR]: "0xDf6163ddD3Ebcb552Cc1379a9c65AFe68683534e",
    [manifest.SABLIER_BATCH_LOCKUP]: "0xF7BA8a7dc96d1939b789b91865bdb05596EBB558",
    [manifest.SABLIER_LOCKUP]: ["0x1f898895eAB949FfD34c29Cf859C035DC4525DF4", 23_094_981],
    [manifest.VESTING_MATH]: "0x5522CA06Ce080800AB59BA4C091e63f6f54C5E6d",
  }),
  get(chains.sepolia.id, {
    [manifest.HELPERS]: "0xf8076E4Fb5cfE8be1C26E61222DC51828Db8C1dc",
    [manifest.LOCKUP_NFT_DESCRIPTOR]: "0x08D3C81626d9Cb19760835e8730Ec0D3F1899976",
    [manifest.SABLIER_BATCH_LOCKUP]: "0xB655ecD83D27f7c683A9605783bd2866a4dCEB04",
    [manifest.SABLIER_LOCKUP]: ["0xd116c275541cdBe7594A202bD6AE4DBca4578462", 7_583_311],
    [manifest.VESTING_MATH]: "0x5522CA06Ce080800AB59BA4C091e63f6f54C5E6d",
  }),
  get(chains.superseedSepolia.id, {
    [manifest.HELPERS]: "0xf8076E4Fb5cfE8be1C26E61222DC51828Db8C1dc",
    [manifest.LOCKUP_NFT_DESCRIPTOR]: "0xc5D8E4317CE4a2E323192A5d856C90372bDE1558",
    [manifest.SABLIER_BATCH_LOCKUP]: "0xB2C6C57ee10B88E8344f34ffeCe39B0C6573c23D",
    [manifest.SABLIER_LOCKUP]: ["0x4E83EC1Ea3B885C1a3698dA7DC42F32575688ABE", 13_532_748],
    [manifest.VESTING_MATH]: "0x5522CA06Ce080800AB59BA4C091e63f6f54C5E6d",
  }),
];
